import type { ProductKind } from "./product-kind";

export type ProductCatalogMode = "retail" | "production" | "online-showcase";

type ListFilters = {
  kind?: ProductKind;
  isSellable?: boolean;
};

export type ProductCatalogConfig = {
  mode: ProductCatalogMode;
  routes: {
    list: string;
    new: string;
    detail: string;
    edit: string;
  };
  labels: {
    section: string;
    listTitle: string;
    listDescription: string;
    createTitle: string;
    createDescription: string;
    editTitle: string;
    editDescription: string;
    detailDescription: string;
    createButton: string;
    breadcrumb: string;
  };
  listFilters: ListFilters;
  showKindColumn: boolean;
  showPriceColumns: boolean;
  showVariantsColumn: boolean;
  lockedKind?: ProductKind;
  hideOptionsInForm: boolean;
  /** ຟອມສั้ນ — ເຫຼືອແຕ່ຊື່, ລາຍລະອຽດ, slug, ສະຖານະ */
  simplifiedForm: boolean;
  requiresShowcaseServices: boolean;
  formLabels: {
    sectionTitle: string;
    submit: string;
    imagesHint?: string;
  };
};

export const PRODUCT_CATALOG_CONFIG: Record<
  ProductCatalogMode,
  ProductCatalogConfig
> = {
  retail: {
    mode: "retail",
    routes: {
      list: "/app/catalog/products",
      new: "/app/catalog/products/new",
      detail: "/app/catalog/products/$id",
      edit: "/app/catalog/products/$id/edit",
    },
    labels: {
      section: "ສິນຄ້າ",
      listTitle: "ສິນຄ້າ",
      listDescription: "ລາຍການສິນຄ້າຂາຍ, ຄົ້ນຫາ ແລະ ກອງຕາມສະຖານະ.",
      createTitle: "ສ້າງສິນຄ້າໃໝ່",
      createDescription: "ເພີ່ມລາຍລະອຽດ, ຮູບພາບ ແລະ ຕົວເລືອກສຳລັບສິນຄ້າໃໝ່",
      editTitle: "ແກ້ໄຂສິນຄ້າ",
      editDescription: "ອັບເດດລາຍລະອຽດ, ຮູບພາບ ແລະ ຕົວເລືອກຂອງສິນຄ້າ",
      detailDescription: "ຈັດການລາຍລະອຽດ, ລາຄາ ແລະ ຮູບພາບສິນຄ້າ",
      createButton: "ສ້າງສິນຄ້າ",
      breadcrumb: "ສິນຄ້າ",
    },
    listFilters: { isSellable: true },
    showKindColumn: true,
    showPriceColumns: true,
    showVariantsColumn: true,
    hideOptionsInForm: false,
    simplifiedForm: false,
    requiresShowcaseServices: false,
    formLabels: {
      sectionTitle: "ຂໍ້ມູນສິນຄ້າ",
      submit: "ບັນທຶກສິນຄ້າ",
    },
  },
  production: {
    mode: "production",
    routes: {
      list: "/app/catalog/production",
      new: "/app/catalog/production/new",
      detail: "/app/catalog/production/$id",
      edit: "/app/catalog/production/$id/edit",
    },
    labels: {
      section: "ແບບຮັບຜະລິດ",
      listTitle: "ແບບຮັບຜະລິດ",
      listDescription:
        "ຈັດການແບບຕົວຢ່າງສຳລັບ About Showcase — ບໍ່ຂາຍໃນຮ້ານ.",
      createTitle: "ສ້າງແບບຮັບຜະລິດ",
      createDescription: "ເພີ່ມຊື່, ລາຍລະອຽດ ແລະ ຮູບພາບສຳລັບແບບຕົວຢ່າງ",
      editTitle: "ແກ້ໄຂແບບຮັບຜະລິດ",
      editDescription: "ອັບເດດຊື່, ລາຍລະອຽດ ແລະ ຮູບພາບແບບຕົວຢ່າງ",
      detailDescription: "ຈັດການລາຍລະອຽດ ແລະ ຮູບພາບແບບຕົວຢ່າງ",
      createButton: "ສ້າງແບບໃໝ່",
      breadcrumb: "ແບບຮັບຜະລິດ",
    },
    listFilters: { kind: "production" },
    showKindColumn: false,
    showPriceColumns: false,
    showVariantsColumn: false,
    lockedKind: "production",
    hideOptionsInForm: true,
    simplifiedForm: true,
    requiresShowcaseServices: true,
    formLabels: {
      sectionTitle: "ຂໍ້ມູນແບບຕົວຢ່າງ",
      submit: "ບັນທຶກແບບ",
      imagesHint:
        "ອັບໂຫຼດຮູບພາບໄດ້ຫຼັງບັນທຶກໃນຫນ້າລາຍລະອຽດ",
    },
  },
  "online-showcase": {
    mode: "online-showcase",
    routes: {
      list: "/app/catalog/online-showcase",
      new: "/app/catalog/online-showcase/new",
      detail: "/app/catalog/online-showcase/$id",
      edit: "/app/catalog/online-showcase/$id/edit",
    },
    labels: {
      section: "ແບບສິນຄ້າອອນໄລນ໌",
      listTitle: "ແບບສິນຄ້າອອນໄລນ໌",
      listDescription:
        "ຈັດການແບບຕົວຢ່າງສິນຄ້າອອນໄລນ໌ສຳລັບ About Showcase — ບໍ່ຂາຍໃນຮ້ານ.",
      createTitle: "ສ້າງແບບສິນຄ້າອອນໄລນ໌",
      createDescription: "ເພີ່ມຊື່, ລາຍລະອຽດ ແລະ ຮູບພາບສຳລັບແບບຕົວຢ່າງ",
      editTitle: "ແກ້ໄຂແບບສິນຄ້າອອນໄລນ໌",
      editDescription: "ອັບເດດຊື່, ລາຍລະອຽດ ແລະ ຮູບພາບແບບຕົວຢ່າງ",
      detailDescription: "ຈັດການລາຍລະອຽດ ແລະ ຮູບພາບແບບຕົວຢ່າງ",
      createButton: "ສ້າງແບບໃໝ່",
      breadcrumb: "ແບບສິນຄ້າອອນໄລນ໌",
    },
    listFilters: { kind: "online-showcase" },
    showKindColumn: false,
    showPriceColumns: false,
    showVariantsColumn: false,
    lockedKind: "online-showcase",
    hideOptionsInForm: true,
    simplifiedForm: true,
    requiresShowcaseServices: true,
    formLabels: {
      sectionTitle: "ຂໍ້ມູນແບບຕົວຢ່າງ",
      submit: "ບັນທຶກແບບ",
      imagesHint:
        "ອັບໂຫຼດຮູບພາບໄດ້ຫຼັງບັນທຶກໃນຫນ້າລາຍລະອຽດ",
    },
  },
};

export function getProductCatalogConfig(
  mode: ProductCatalogMode = "retail",
): ProductCatalogConfig {
  return PRODUCT_CATALOG_CONFIG[mode];
}

export function getCatalogModeForKind(
  kind: ProductKind | undefined,
): ProductCatalogMode {
  if (kind === "production") return "production";
  if (kind === "online-showcase") return "online-showcase";
  return "retail";
}

export function isCatalogModeMatch(
  catalogMode: ProductCatalogMode,
  kind: ProductKind | undefined,
): boolean {
  return getCatalogModeForKind(kind) === catalogMode;
}

export function isProductionCatalogProduct(
  kind: ProductKind | undefined,
): boolean {
  return kind === "production";
}

export function isOnlineShowcaseCatalogProduct(
  kind: ProductKind | undefined,
): boolean {
  return kind === "online-showcase";
}
